'use client'

import { Avatar } from '@repo/ui/Avatar'
import { CalendarSyncIcon } from '@repo/ui/CalendarSyncIcon'
import { CircledCheckIcon } from '@repo/ui/CircledCheckIcon'
import { ClientMatterDetailsMenuItem } from '@repo/ui/ClientMatterDetailsMenuItem'
import { formatDate, formatTime, dateIsToday, dateIsInThePast } from '@repo/utils/time'
import { MiniMatterCard } from '@repo/ui/MiniMatterCard'
import { Pill } from '@repo/ui/Pill'
import { useFeeEarnerStore } from '@repo/store/useFeeEarnerStore'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMatterWorkflowStore } from '@repo/store/useMatterWorkflowStore'
import { useState, useEffect } from 'react'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const KeyDateReadState = ({ keyDate }) => {
  const { keyDateLocationsById, keyDateTypes, fetchKeyDateTypeById, toggleKeyDatesDrawer } = useKeyDateStore()
  const { feeEarners } = useFeeEarnerStore()
  const { matters, fetchMatter } = useMattersStore()
  const width = 'calc(50% - 12px)'
  const { navigateWithClientMatterNo } = useUrlStore()
  const { getWorkflowLink } = useMatterWorkflowStore()

  const {
    clientNo,
    createdBy,
    createdDate,
    criminalNotes,
    date,
    dateMet,
    durationMinutes,
    feeEarnerId,
    isAllDay,
    keyDateLocationId,
    keyDateTypeId,
    matterNo,
    mustAttend,
    notes,
    outlookEventId,
    published,
    startTime,
    actionLinkedTaskId,
    taskPositionIndex,
    uniqueWorkflowCount,
    updatedBy,
    updatedDate,
    userId,
    workflowId,
    workflowTaskIsTicked
  } = keyDate || {}
  const keyDateLocation = keyDateLocationsById?.[keyDateLocationId]?.description || '-'
  const feeEarner = feeEarners?.find((fe) => fe.feeEarnerId === feeEarnerId) || null
  const feeEarnerDescription = feeEarner?.description || '-'
  const feeEarnerLegalAidPin = feeEarner?.legalAidPin || '-'
  const [keyDateType, setKeyDateType] = useState(null)
  const [matter, setMatter] = useState(null)
  const [hasWorkflowTask, setHasWorkflowTask] = useState(false)

  let status = 'Due soon'
  let statusColor = 'success.main'

  if (dateMet) {
    status = 'Date met'
    statusColor = 'secondary.main'
  } else if (dateIsToday(date)) {
    status = 'Due today'
    statusColor = 'warning.light'
  } else if (dateIsInThePast(date)) {
    status = 'Overdue'
    statusColor = 'error.main'
  }

  const calculateEndTime = () => {
    if (startTime && durationMinutes) {
      const [hours, minutes] = startTime.split(':')
      const updatedHours = Number(hours) + Math.floor(durationMinutes / 60)
      const updatedMinutes = Number(minutes) + (durationMinutes % 60)
      const endTime = `${updatedHours}:${updatedMinutes}`
      return formatTime(`${endTime}:00`)
    }

    return null
  }

  const formattedDate = formatDate(date) || null
  let time = null

  if (isAllDay) time = 'All day'
  else if (startTime) {
    time = formatTime(`${startTime}:00`)

    const endTime = calculateEndTime()
    if (endTime) time += ` - ${endTime}`
  }

  useEffect(() => {
    if (!keyDate || !clientNo) return

    const clientMatterNo = `${keyDate.clientNo}-${keyDate.matterNo}`

    if (matters?.[`${clientMatterNo}`]) {
      setMatter(matters[`${clientMatterNo}`])
      return
    }

    const fetchAndSetMatter = async () => {
      const matter = await fetchMatter({ clientMatterNo })
      setMatter(matter)
    }

    if (clientMatterNo) fetchAndSetMatter()
  }, [matters, clientNo, matterNo, fetchMatter, keyDate])

  useEffect(() => {
    if (!keyDateTypeId || !keyDateTypes) {
      setKeyDateType(null)
      return
    }

    let keyDateType = keyDateTypes[keyDateTypeId]?.description || null

    if (keyDateType) {
      setKeyDateType(keyDateType)
      return
    }

    const fetchAndSetKeyDateType = async () => {
      try {
        const keyDateType = await fetchKeyDateTypeById({ keyDateTypeId })
        if (keyDateType?.description) {
          setKeyDateType(keyDateType.description)
        } else {
          setKeyDateType(null)
        }
      } catch (error) {
        console.error(error)
        setKeyDateType(null)
      }
    }

    fetchAndSetKeyDateType()
  }, [keyDateTypeId, keyDateTypes, fetchKeyDateTypeById])

  useEffect(() => {
    setHasWorkflowTask(
      clientNo &&
        matterNo &&
        actionLinkedTaskId &&
        taskPositionIndex &&
        workflowId &&
        uniqueWorkflowCount &&
        !workflowTaskIsTicked
    )
  }, [clientNo, matterNo, actionLinkedTaskId, taskPositionIndex, workflowId, uniqueWorkflowCount, workflowTaskIsTicked])

  if (!keyDate) return <MUIStack data-testid="KeyDateReadState" />

  const hrStyle = { width: '100%', margin: '-4px 0 8px', border: '1px solid rgba(5, 28, 51, 0.06)' }

  return (
    <MUIStack
      data-testid="KeyDateReadState"
      direction="row"
      justifyContent="space-between"
      gap={5}
      sx={{ width: '100%', position: 'relative', padding: '0 20px 20px' }}
      flexWrap="wrap"
    >
      <ClientMatterDetailsMenuItem
        title="Key date description"
        width={width}
        label={notes || '-'}
        variant="large"
        noWrap={false}
      />

      <ClientMatterDetailsMenuItem title="Status" width={width} variant="large">
        <MUIStack direction="row" gap={4} sx={{ mt: 1 }}>
          <Pill
            label={status}
            backgroundColor={statusColor}
            color={'white'}
            icon={
              dateMet ? <CircledCheckIcon sx={{ width: '14px', height: '14px', color: 'white', ml: 1, mr: 0 }} /> : null
            }
          />
        </MUIStack>
      </ClientMatterDetailsMenuItem>

      <ClientMatterDetailsMenuItem
        title={'Date'}
        width={width}
        label={formattedDate || '-'}
        variant="large"
        noWrap={false}
      />

      <ClientMatterDetailsMenuItem title={'Time'} width={width} label={time || '-'} variant="large" noWrap={false} />

      {outlookEventId ? (
        <MUIStack
          direction="row"
          gap={1}
          justifyContent="flex-start"
          alignItems="center"
          sx={{ mt: -6, padding: '0 12px', width: '100%', opacity: 0.8 }}
        >
          <CalendarSyncIcon sx={{ width: '16px', height: '16px' }} />
          <MUITypography variant="body2" sx={{ mb: 0 }}>
            Synced to Outlook
          </MUITypography>
        </MUIStack>
      ) : null}

      <hr style={hrStyle} />

      <ClientMatterDetailsMenuItem
        title="Associated matter"
        width="100%"
        variant="large"
        enableCopy={false}
        isClientMatterDetailsPill
      >
        <MUIStack direction="row" justifyContent="flex-start">
          {clientNo && matter ? (
            <MiniMatterCard
              {...matter}
              disableDefaultClickHandler
              sx={{ backgroundColor: 'rgba(61, 81, 123, 0.04) !important', cursor: 'default' }}
            />
          ) : null}
        </MUIStack>
      </ClientMatterDetailsMenuItem>

      <ClientMatterDetailsMenuItem title="Key date type" width={width} label={keyDateType ? null : '-'} variant="large">
        {keyDateType ? (
          <MUIStack direction="row" gap={4} sx={{ mt: 1 }}>
            <Pill label={keyDateType} backgroundColor="background.mid" />
          </MUIStack>
        ) : null}
      </ClientMatterDetailsMenuItem>

      <ClientMatterDetailsMenuItem
        title="Published to Portal"
        width={width}
        label={published ? 'Yes' : 'No'}
        variant="large"
      />

      <ClientMatterDetailsMenuItem title="Key date fee earner" width={width} variant="large">
        <MUIStack direction="row" gap={2} sx={{ mt: 1, mb: 1 }} alignItems="center">
          <Avatar feeEarnerId={feeEarnerId} name={feeEarnerDescription} size={'32px'} />
          <MUITypography variant="body2" sx={{ mb: 0, textAlign: 'left' }} whiteSpace="pre-line">
            {feeEarnerDescription}
          </MUITypography>
        </MUIStack>
      </ClientMatterDetailsMenuItem>

      <ClientMatterDetailsMenuItem
        title="Fee earner Legal Aid PIN"
        width={width}
        variant="large"
        label={feeEarnerLegalAidPin}
      />

      <hr style={hrStyle} />

      <ClientMatterDetailsMenuItem
        title="Attendance"
        width={width}
        variant="large"
        label={mustAttend === true || mustAttend === false ? null : '-'}
      >
        {mustAttend === true || mustAttend === false ? (
          <MUIStack direction="row" gap={4} sx={{ mt: 1 }}>
            <Pill
              label={mustAttend === true ? 'Must attend' : 'Optional attendance'}
              backgroundColor={mustAttend === true ? 'info.dark' : 'background.mid'}
              color={mustAttend === true ? 'white' : 'black'}
            />
          </MUIStack>
        ) : null}
      </ClientMatterDetailsMenuItem>

      <ClientMatterDetailsMenuItem title="Location" width={width} label={keyDateLocation} variant="large" />

      <hr style={hrStyle} />

      <ClientMatterDetailsMenuItem
        title="Additional details"
        width={width}
        label={criminalNotes || '-'}
        variant="large"
        noWrap={false}
      />

      <ClientMatterDetailsMenuItem
        title="Workflow task"
        width={width}
        label={hasWorkflowTask ? 'Run task' : workflowTaskIsTicked ? 'Complete' : '-'}
        variant="large"
        enableCopy={false}
        enableLink={hasWorkflowTask}
        showHoverIcon={hasWorkflowTask}
        onClick={
          hasWorkflowTask
            ? () => {
                toggleKeyDatesDrawer(false)

                const route = getWorkflowLink({
                  clientNo,
                  matterNo,
                  actionLinkedTaskId,
                  taskPositionIndex,
                  workflowId,
                  uniqueWorkflowCount
                })

                navigateWithClientMatterNo({
                  route,
                  clientMatterNo: `${clientNo}-${matterNo}`,
                  appendClientMatterNo: true
                })
              }
            : null
        }
        sx={{ cursor: hasWorkflowTask ? 'pointer' : 'default' }}
      />

      <hr style={hrStyle} />

      <ClientMatterDetailsMenuItem
        title="Created by"
        width={width}
        label={createdBy || userId || '-'}
        variant="large"
      />

      <ClientMatterDetailsMenuItem
        title="Created date"
        width={width}
        label={formatDate(createdDate, 'EEE d MMM yyyy, h:mm a') || '-'}
        variant="large"
      />

      <ClientMatterDetailsMenuItem
        title="Updated by"
        width={width}
        label={updatedBy || (updatedDate ? createdBy || userId : '-')}
        variant="large"
      />

      <ClientMatterDetailsMenuItem
        title="Updated date"
        width={width}
        label={formatDate(updatedDate, 'EEE d MMM yyyy, h:mm a') || '-'}
        variant="large"
      />
    </MUIStack>
  )
}

KeyDateReadState.propTypes = {
  keyDate: PropTypes.object
}
